import type {
  ReCaptcha,
  RecaptchaResult,
  ValidationRequest,
  ValidationResponse,
  WindowWithRecaptcha,
} from '../types/reCAPTCHA';
/**
 * Ensures that the reCAPTCHA script is loaded before proceeding.
 * This function polls the window object every 100ms to check if reCAPTCHA is loaded.
 *
 * @returns {Promise<void>} A promise that resolves when reCAPTCHA is loaded
 * @throws {Error} If the window object is not available (e.g., in a non-browser environment)
 *
 * @example
 * ```typescript
 * await ensureRecaptchaLoaded();
 * // Now safe to use reCAPTCHA
 * ```
 */
export async function ensureRecaptchaLoaded(): Promise<void> {
  const typedWindow = window as WindowWithRecaptcha;

  if (typedWindow.recaptchaLoaded) {
    return;
  }

  return new Promise<void>((resolve) => {
    const checkRecaptcha = setInterval(() => {
      if (typedWindow.recaptchaLoaded) {
        clearInterval(checkRecaptcha);
        resolve();
      }
    }, 100);
  });
}

/**
 * Executes reCAPTCHA and returns the token along with any potential errors.
 *
 * @param {string} siteKey - The reCAPTCHA site key provided by Google
 * @returns {Promise<RecaptchaResult>} Object containing either a token or an error
 * @throws {Error} If grecaptcha is not properly loaded or initialized
 *
 * @example
 * ```typescript
 * const siteKey = 'your-recaptcha-site-key';
 * const { token, error } = await executeRecaptcha(siteKey);
 * if (error) {
 *   console.error('ReCAPTCHA error:', error);
 *   return;
 * }
 * // Use token for verification
 * ```
 */
export function executeRecaptcha(siteKey: string): Promise<RecaptchaResult> {
  return new Promise<RecaptchaResult>((resolve) => {
    window.grecaptcha.ready(async () => {
      try {
        const token = await window.grecaptcha.execute(siteKey, { action: 'submit' });
        resolve({ token, error: null });
      } catch (error) {
        resolve({ token: null, error: error as Error });
      }
    });
  });
}

/**
 * Validates the reCAPTCHA token with the server.
 *
 * @param {string} token - The reCAPTCHA token to validate
 * @param {string} siteKey - The reCAPTCHA site key for validation
 * @returns {Promise<ValidationResponse>} The validation result and any error
 *
 * @example
 * ```typescript
 * const { token } = await executeRecaptcha(siteKey);
 * const { data, error } = await validateRecaptcha(token, siteKey);
 * if (error) {
 *   console.error('Validation failed:', error);
 *   return;
 * }
 * // Process validation data
 * ```
 */
export async function validateRecaptcha(
  token: string,
  siteKey: string
): Promise<ValidationResponse> {
  const API_URL =
    'https://consumers-gateway-4cygfe52ia-ew.a.run.app:443/pointa.pa.services.public.v1.FormsService/ValidateFormSubmission';

  const payload: ValidationRequest = {
    token,
    recaptchaAction: 'submit',
    recaptchaKey: siteKey,
  };

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

  export const handleRecaptchaVerification = async (siteKey: string) => {
    await ensureRecaptchaLoaded();
    const { token, error: recaptchaError } = await executeRecaptcha(siteKey);

    if (recaptchaError) {
      console.error('reCAPTCHA error:', recaptchaError);
      throw new Error('reCAPTCHA execution failed');
    }

    const { data: validationData, error: validationError } = await validateRecaptcha(
      token!,
      siteKey
    );

    if (validationError || validationData.score <= 0.8) {
      console.error('Validation error:', validationError);
      throw new Error(validationError ? 'reCAPTCHA validation failed' : 'reCAPTCHA score too low');
    }
  };

